import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Progress,
  Badge,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import RABillAside from "./raBillAside";
import { formatCurrency } from "../../utils/Utils";
import { mockRABills } from "./mockRABills";

const { Option } = Select;
const { RangePicker } = DatePicker;

const RABillsOutstanding: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [outstandingRABills, setOutstandingRABills] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    paymentStatus: "",
    contractor: "",
    raBillNumber: "",
    woNumber: "",
    dateRange: null as any,
    site: "",
    agingDays: "",
  });

  useEffect(() => {
    loadOutstandingRABills();
  }, []);

  const loadOutstandingRABills = () => {
    setLoading(true);
    try {
      // Filter RA Bills that have outstanding payments (Unpaid or Partially Paid)
      const outstandingBills = mockRABills
        .filter(
          (bill) =>
            ["Approved", "Posted", "Paid"].includes(bill.status) &&
            bill.balanceAmount > 0
        )
        .map((bill) => ({
          ...bill,
          // Calculate days since approval for aging analysis
          daysSinceApproval: bill.approvedOn
            ? dayjs().diff(dayjs(bill.approvedOn), "days")
            : 0,
          // Calculate expected payment date (30 days from approval)
          expectedPaymentDate: bill.approvedOn
            ? dayjs(bill.approvedOn).add(30, "days").format("YYYY-MM-DD")
            : null,
          // Check if overdue
          isOverdue: bill.approvedOn
            ? dayjs().diff(dayjs(bill.approvedOn), "days") > 30
            : false,
        }));

      setOutstandingRABills(outstandingBills);
    } catch (error) {
      console.error("Error loading outstanding RA Bills:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      "Partially Paid": "warning",
      Unpaid: "error",
    };
    return statusColors[status] || "default";
  };

  const getAgingColor = (days: number): string => {
    if (days > 60) return "#ff4d4f";
    if (days > 30) return "#faad14";
    return "#52c41a";
  };

  const getPaymentProgress = (
    paidAmount: number,
    totalAmount: number
  ): number => {
    // Handle null, undefined, or NaN values
    const safePaidAmount = Number(paidAmount) || 0;
    const safeTotalAmount = Number(totalAmount) || 0;

    if (safeTotalAmount === 0) return 0;

    const percentage = Math.round((safePaidAmount / safeTotalAmount) * 100);

    // Ensure the result is a valid number
    return isNaN(percentage) ? 0 : percentage;
  };

  const handleViewRABill = (record: any) => {
    navigate(
      `${process.env.PUBLIC_URL}/ra-bills-view-details/${record.raBillId}?mode=view`
    );
  };

  const handleFollowUp = (record: any) => {
    // Implementation for follow-up action (email, notification, etc.)
    console.log("Follow up for:", record.raBillNumber);
  };

  const resetFilters = () => {
    setFilters({
      paymentStatus: "",
      contractor: "",
      raBillNumber: "",
      woNumber: "",
      dateRange: null,
      site: "",
      agingDays: "",
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be implemented here
  };

  // KPI calculations
  const totalOutstanding = outstandingRABills.length;
  const totalOutstandingValue = outstandingRABills.reduce(
    (sum, bill) => sum + (Number(bill.balanceAmount) || 0),
    0
  );
  const overdueCount = outstandingRABills.filter(
    (bill) => bill.isOverdue
  ).length;
  const avgAgingDays =
    outstandingRABills.length > 0
      ? Math.round(
          outstandingRABills.reduce(
            (sum, bill) => sum + (Number(bill.daysSinceApproval) || 0),
            0
          ) / outstandingRABills.length
        )
      : 0;

  const columns: TableColumnsType<any> = [
    {
      title: "RA Bill #",
      dataIndex: "raBillSequenceId",
      key: "raBillSequenceId",
      width: 180,
      fixed: "left",
      render: (text: string, record: any) => (
        <div>
          <span
            className="inlineTableCellLink"
            id={`raBillOutstanding-${record?.raBillId}`}
            onClick={() => handleViewRABill(record)}
          >
            {text}
          </span>
          {record.isOverdue && (
            <div>
              <Tag color="red">OVERDUE</Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 180,
      // render: (text, record) => (
      //   <div>
      //     <div className="fw-bold text-primary">{text}</div>
      //     <div className="text-muted small mt-1">{record.contractor}</div>
      //   </div>
      // ),
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 200,
      render: (_, record) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          <div className="text-muted small">{record.projectName}</div>
        </div>
      ),
    },
    {
      title: "RA Bill Value",
      dataIndex: "grossTotal",
      key: "grossTotal",
      width: 140,
      align: "right",
      render: (value) => (
        <div className="fw-bold">{formatCurrency(value, "INR")}</div>
      ),
    },
    {
      title: "Paid Amount",
      dataIndex: "paidAmount",
      key: "paidAmount",
      width: 130,
      align: "right",
      render: (paidAmount, record) => (
        <div>
          <div className="fw-bold text-success">
            {formatCurrency(paidAmount, "INR")}
          </div>
          <div className="text-muted small">
            {(() => {
              const percentage = getPaymentProgress(paidAmount, record.currentBillValue);
              return `${percentage}% paid`;
            })()}
          </div>
        </div>
      ),
    },
    {
      title: "Outstanding Amount",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
      width: 150,
      align: "right",
      render: (balance, record) => (
        <div>
          <div className="fw-bold text-danger">
            {formatCurrency(balance, "INR")}
          </div>
          <Progress
            percent={
              100 -
              getPaymentProgress(record.paidAmount, record.grossTotal)
            }
            size="small"
            strokeColor="#ff4d4f"
            showInfo={false}
          />
        </div>
      ),
      sorter: (a, b) => a.balanceAmount - b.balanceAmount,
    },
    {
      title: "Approved Date",
      dataIndex: "approvedAt",
      key: "approvedAt",
      width: 130,
      render: (date) => (date ? dayjs(date).format("DD-MMM-YYYY") : "---"),
      sorter: (a, b) => dayjs(a.approvedAt).unix() - dayjs(b.approvedAt).unix(),
    },
    {
      title: "Expected Payment",
      key: "expectedPayment",
      width: 140,
      render: (_, record) => (
        <div>
          {record.expectedPaymentDate ? (
            <div className={record.isOverdue ? "text-danger" : ""}>
              {dayjs(record.expectedPaymentDate).format("DD-MMM-YYYY")}
            </div>
          ) : (
            <span className="text-muted">---</span>
          )}
        </div>
      ),
    },
    {
      title: "Aging (Days)",
      key: "slaAgingForOutstanding",
      dataIndex: "slaAgingForOutstanding",
      width: 120,
      align: "center",
      render: (_, record) => {
        const agingDays = record.daysSinceApproval || 0;
        return (
          <Badge count={`${agingDays}d`} color={getAgingColor(agingDays)} />
        );
      },
      sorter: (a, b) => (a.daysSinceApproval || 0) - (b.daysSinceApproval || 0),
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: 130,
      render: (status) => (
        <Tag color={getPaymentStatusColor(status)}>{status}</Tag>
      ),
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   fixed: "right",
    //   width: 180,
    //   render: (_, record) => (
    //     <Space wrap size="small">
    //       <Button
    //         type="primary"
    //         size="small"
    //         icon={<EyeOutlined />}
    //         onClick={() => handleViewRABill(record)}
    //       >
    //         View
    //       </Button>
    //       <Button
    //         size="small"
    //         icon={<PhoneOutlined />}
    //         onClick={() => handleFollowUp(record)}
    //       >
    //         Follow Up
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="ra-bills-outstanding"
      title="Outstanding RA Bills"
      subtitle="Track and manage unpaid and partially paid Running Account Bills"
      columns={columns}
      dataSource={outstandingRABills}
      loading={loading}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <RABillAside updateSm={setSm} sm={sm} />
        </div>
      }
      extra={{
        tableScrollX: 1800,
        showSearch: true,
        searchPlaceholder: "Search by RA Bill#, WO#, or Contractor...",
        kpiWidgets: [
          {
            title: "Total Outstanding Bills",
            value: totalOutstanding,
            icon: <ClockCircleOutlined />,
            color: "#faad14",
          },
          {
            title: "Outstanding Value",
            value: formatCurrency(totalOutstandingValue, "INR"),
            icon: <DollarOutlined />,
            color: "#ff4d4f",
          },
          {
            title: "Overdue Bills",
            value: overdueCount,
            icon: <CalendarOutlined />,
            color: "#ff4d4f",
          },
          {
            title: "Average Aging",
            value: `${avgAgingDays} days`,
            icon: <UserOutlined />,
            color: "#722ed1",
          },
        ],
        advancedFilter: {
          visible: drawerVisible,
          onClose: () => setDrawerVisible(false),
          onOpen: () => setDrawerVisible(true),
          content: (
            <div className="p-4">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <label className="form-label">Payment Status</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Payment Status"
                    value={filters.paymentStatus}
                    onChange={(value) =>
                      setFilters({ ...filters, paymentStatus: value })
                    }
                  >
                    <Option value="Partially Paid">Partially Paid</Option>
                    <Option value="Unpaid">Unpaid</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Contractor</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contractor"
                    value={filters.contractor}
                    onChange={(value) =>
                      setFilters({ ...filters, contractor: value })
                    }
                  >
                    <Option value="ABC Infra Ltd">ABC Infra Ltd</Option>
                    <Option value="XYZ Constructions">XYZ Constructions</Option>
                    <Option value="BuildTech Pvt Ltd">BuildTech Pvt Ltd</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">RA Bill #</label>
                  <Input
                    placeholder="Enter RA Bill Number"
                    value={filters.raBillNumber}
                    onChange={(e) =>
                      setFilters({ ...filters, raBillNumber: e.target.value })
                    }
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Work Order #</label>
                  <Input
                    placeholder="Enter WO Number"
                    value={filters.woNumber}
                    onChange={(e) =>
                      setFilters({ ...filters, woNumber: e.target.value })
                    }
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Site</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Site"
                    value={filters.site}
                    onChange={(value) =>
                      setFilters({ ...filters, site: value })
                    }
                  >
                    <Option value="Urban Rise - Tower A">
                      Urban Rise - Tower A
                    </Option>
                    <Option value="Urban Rise - Tower B">
                      Urban Rise - Tower B
                    </Option>
                    <Option value="Urban Rise - Tower C">
                      Urban Rise - Tower C
                    </Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Aging Days</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Aging Range"
                    value={filters.agingDays}
                    onChange={(value) =>
                      setFilters({ ...filters, agingDays: value })
                    }
                  >
                    <Option value="0-30">0-30 days</Option>
                    <Option value="31-60">31-60 days</Option>
                    <Option value="61-90">61-90 days</Option>
                    <Option value="90+">90+ days</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Approval Date Range</label>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={filters.dateRange}
                    onChange={(dates) =>
                      setFilters({ ...filters, dateRange: dates })
                    }
                    placeholder={["From Date", "To Date"]}
                  />
                </Col>
              </Row>
              <div className="mt-4 d-flex gap-2">
                <Button
                  type="primary"
                  onClick={applyFilters}
                  style={{ flex: 1 }}
                >
                  Apply Filters
                </Button>
                <Button onClick={resetFilters} style={{ flex: 1 }}>
                  Reset
                </Button>
              </div>
            </div>
          ),
        },
      }}
    />
  );
};

export default RABillsOutstanding;
